import { Button, FormHelperText, IconButton } from "@mui/material"
import { FieldsRow, FormRoleConfContext } from "@pharmupp/p3-front-commons"
import { SearchOrCreateUserField } from "@pharmupp/p3-user-domain"
import { useEffect, useState } from "react"
import { PlusCircle, Trash } from "react-feather"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"
import { UserServiceParams } from "../../../../core/user-service.params"
import { detailsFields as fields } from "../details.const"

interface HolderListProps {
  initialConf: Record<
    string,
    {
      disabled?: boolean
      displayOnlyValue?: boolean
      hideActions?: boolean
    }
  >
}

export const HolderList = ({ initialConf }: HolderListProps) => {
  const {
    formState: { errors, isDirty },
    control,
  } = useFormContext()

  const {
    fields: holderFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: fields.otherHolders,
    keyName: "arrayKey",
  })

  const [conf, setConf] = useState({ ...initialConf })
  useEffect(() => {
    if (!isDirty && conf.oldHolderRules) {
      holderFields.forEach((_, index) => {
        setConf((previousConf) => ({
          ...previousConf,
          [`${fields.otherHolders}.${index}`]: { ...previousConf.oldHolderRules },
        }))
      })
    }
  }, [conf.oldHolderRules, holderFields, isDirty])

  const addNewOtherHolder = () => {
    if (holderFields.length < 3) {
      const newHolderIndex = holderFields.length
      if (conf.newHolderRules) {
        setConf((previousConf) => ({
          ...previousConf,
          [`${fields.otherHolders}.${newHolderIndex}`]: {
            ...previousConf.newHolderRules,
          },
        }))
      }
      append({ id: null })
    }
  }

  const primaryHolder = useWatch({ name: fields.primaryHolder.root })
  const otherHolders = useWatch({ name: fields.otherHolders }) as ({
    id: string
  } | null)[]
  const excludedIds =
    otherHolders?.flatMap((holder) => (holder ? holder.id : [])) ?? []
  if (primaryHolder) {
    excludedIds.push(primaryHolder.id)
  }

  const shouldShowRemoveButton = (index: number) =>
    shouldDisplayCTA &&
    (!conf[`${fields.otherHolders}.${index}`] ||
      !conf[`${fields.otherHolders}.${index}`].displayOnlyValue)

  const shouldDisplayCTA = !conf[fields.primaryHolder.root]?.hideActions

  return (
    <FormRoleConfContext.Provider value={conf}>
      <SearchOrCreateUserField
        name={fields.primaryHolder.root}
        label="Titulaire principal"
        userServiceParams={UserServiceParams}
        excludedUserIds={excludedIds || []}
        required
        allowCreate={false}
      />
      {!!errors.otherHolders?.message && (
        <FormHelperText error>{errors.otherHolders?.message}</FormHelperText>
      )}
      {holderFields.map((item, index) => (
        <FieldsRow key={item?.arrayKey} alignItems="flex-start">
          <SearchOrCreateUserField
            name={`${fields.otherHolders}.${index}`}
            label="Autre titulaire"
            userServiceParams={UserServiceParams}
            excludedUserIds={excludedIds || []}
            allowCreate={false}
          />
          {shouldShowRemoveButton(index) && (
            <IconButton
              color="primary"
              onClick={() => remove(index)}
              data-testid={`delete-holder-${index}`}
              sx={{ p: 2 }}
            >
              <Trash />
            </IconButton>
          )}
        </FieldsRow>
      ))}
      {shouldDisplayCTA && (
        <FieldsRow>
          <Button
            onClick={addNewOtherHolder}
            disabled={holderFields.length >= 3}
            startIcon={<PlusCircle />}
          >
            Ajouter un autre titulaire
          </Button>
        </FieldsRow>
      )}
    </FormRoleConfContext.Provider>
  )
}
